import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from '../routing';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { errorText } from '../utils/errors';
import toast from 'react-hot-toast';
import { ArrowLeft, AlertTriangle, ShieldCheck, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PageHero from '../components/PageHero';
import theme from '../theme';

// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';

// This page is deliberately reachable WITHOUT logging in.
//
// Google Play requires an app that offers account creation to publish a
// deletion route that a person can find and read before installing anything —
// the in-app flow alone is not enough, and the URL is declared in the Data
// safety form. So the explanation renders for everyone, and the destructive
// part appears only once you are signed in.


export default function DeleteAccount() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [status, setStatus]   = useState(null);   // { can_delete, blockers }
  const [checking, setChecking] = useState(false);
  const [password, setPassword] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [working, setWorking] = useState(false);

  const loadStatus = useCallback(async () => {
    if (!user) return;
    setChecking(true);
    try {
      setStatus(await api.get('/auth/account/deletion-status'));
    } catch (err) {
      // An outage must not be presented as "you may delete" — leaving status
      // null keeps the button disabled and shows the retry below.
      setStatus(null);
      toast.error(errorText(err, t('deleteAccount.statusFailed')));
    } finally {
      setChecking(false);
    }
  }, [user, t]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const submit = async (e) => {
    e.preventDefault();
    if (working) return;
    setWorking(true);
    try {
      await api.delete('/auth/account', { data: { password, confirm: true } });
      toast.success(t('deleteAccount.doneToast'));
      // The token is dead server-side the moment this returns, so clear the
      // session rather than letting the next request 401 into a redirect.
      logout();
      navigate('/');
    } catch (err) {
      if (err && err.error === 'DELETION_BLOCKED') {
        setStatus({ can_delete: false, blockers: err.blockers || [] });
        toast.error(t('deleteAccount.blockedToast'));
      } else {
        toast.error(errorText(err, t('deleteAccount.failedToast')));
      }
      // The password box is deliberately NOT cleared: the overwhelmingly common
      // failure here is a mistyped password, and wiping the field made the fix
      // "type the whole thing again".
    } finally {
      setWorking(false);
    }
  };

  const card = {
    background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4,
    padding: 32, marginBottom: 24,
  };
  const label = { display: 'block', fontSize: 12, letterSpacing: '0.08em', color: theme.grey400, marginBottom: 8 };
  const input = {
    width: '100%', padding: '12px 14px', border: `1px solid ${theme.sand}`,
    borderRadius: 3, fontSize: 14, fontFamily: 'inherit', background: theme.cream,
  };

  const blockerLabel = (code) => t(`deleteAccount.blockers.${code}`, {
    defaultValue: t('deleteAccount.blockers.GENERIC'),
  });

  return (
    <div style={{ minHeight: '100vh', background: theme.cream, fontFamily: theme.font.body, paddingBottom: 80 }}>
      {/* Defined here rather than assumed: an animation name that exists in no
          stylesheet renders as a frozen ring, which reads as a hung page. */}
      <style>{`
        @import url('${FONTS}');
        @keyframes gg-spin { to { transform: rotate(360deg); } }
        .gg-spin { animation: gg-spin 0.8s linear infinite; }
      `}</style>

      <PageHero title={t('deleteAccount.title')} subtitle={t('deleteAccount.intro')} height={260} />

      <div style={{ maxWidth: 640, margin: '0 auto', padding: '40px 24px 0' }}>
        <Link to={user ? '/profile' : '/'} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: theme.grey400, textDecoration: 'none', fontSize: 13, marginBottom: 32 }}>
          <ArrowLeft size={16} /> {user ? t('deleteAccount.backProfile') : t('deleteAccount.backHome')}
        </Link>

        {/* What happens — shown to everyone, logged in or not. */}
        <div style={card}>
          <h2 style={{ fontFamily: theme.font.display, fontSize: 22, margin: '0 0 20px' }}>
            {t('deleteAccount.whatHappens')}
          </h2>

          <p style={{ fontSize: 13, color: theme.ink, fontWeight: 500, margin: '0 0 8px' }}>{t('deleteAccount.removedTitle')}</p>
          <ul style={{ fontSize: 13, color: theme.grey550, lineHeight: 1.9, margin: '0 0 24px', paddingLeft: 20 }}>
            <li>{t('deleteAccount.removed.identity')}</li>
            <li>{t('deleteAccount.removed.password')}</li>
            <li>{t('deleteAccount.removed.card')}</li>
            <li>{t('deleteAccount.removed.mailing')}</li>
          </ul>

          <p style={{ fontSize: 13, color: theme.ink, fontWeight: 500, margin: '0 0 8px' }}>{t('deleteAccount.keptTitle')}</p>
          <ul style={{ fontSize: 13, color: theme.grey550, lineHeight: 1.9, margin: '0 0 12px', paddingLeft: 20 }}>
            <li>{t('deleteAccount.kept.rentals')}</li>
            <li>{t('deleteAccount.kept.reviews')}</li>
          </ul>
          <p style={{ fontSize: 12, color: theme.grey400, lineHeight: 1.7, margin: 0 }}>
            {t('deleteAccount.keptWhy')} <Link to="/privacy" style={{ color: theme.gold }}>{t('deleteAccount.privacyLink')}</Link>
          </p>
        </div>

        {!user && (
          <div style={{ ...card, textAlign: 'center' }}>
            <p style={{ fontSize: 14, color: theme.grey550, marginBottom: 20 }}>{t('deleteAccount.signInPrompt')}</p>
            <Link to="/login" style={{ display: 'inline-block', background: theme.ink, color: theme.white, padding: '14px 32px', textDecoration: 'none', fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              {t('deleteAccount.signIn')}
            </Link>
            {/* Someone locked out of their account still has an erasure right,
                and it cannot depend on being able to log in. */}
            <p style={{ fontSize: 12, color: theme.grey400, marginTop: 24, lineHeight: 1.7 }}>
              {t('deleteAccount.cannotSignIn')}{' '}
              <a href="mailto:support@glamgo.hair?subject=Account%20deletion%20request" style={{ color: theme.gold }}>support@glamgo.hair</a>
            </p>
          </div>
        )}

        {user && checking && (
          <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 12, color: theme.grey400, fontSize: 13 }}>
            <Loader2 size={16} className="gg-spin" /> {t('deleteAccount.checking')}
          </div>
        )}

        {user && !checking && status && !status.can_delete && (
          <div style={{ ...card, borderColor: theme.redBorder, background: theme.redWashPale }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <AlertTriangle size={18} color={theme.redSoft} />
              <h2 style={{ fontFamily: theme.font.display, fontSize: 22, margin: 0, color: theme.redSoft }}>
                {t('deleteAccount.blockedTitle')}
              </h2>
            </div>
            <p style={{ fontSize: 13, color: theme.grey550, lineHeight: 1.7, marginBottom: 20 }}>
              {t('deleteAccount.blockedIntro')}
            </p>
            {status.blockers.map((b) => (
              <div key={b.code} style={{ borderTop: `1px solid ${theme.redWashWarm}`, paddingTop: 16, marginTop: 16 }}>
                <p style={{ fontSize: 13, fontWeight: 500, color: theme.ink, margin: '0 0 6px' }}>{blockerLabel(b.code)}</p>
                <p style={{ fontSize: 13, color: theme.grey550, lineHeight: 1.7, margin: 0 }}>{b.message}</p>
                {b.code === 'DEPOSIT_OUTSTANDING' && b.total > 0 && (
                  <p style={{ fontSize: 13, color: theme.ink, marginTop: 8 }}>
                    {t('deleteAccount.depositOwed', { amount: Number(b.total).toFixed(2) })}
                  </p>
                )}
              </div>
            ))}
            <Link to="/my-rentals" style={{ display: 'inline-block', marginTop: 24, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.ink }}>
              {t('deleteAccount.viewRentals')} →
            </Link>
          </div>
        )}

        {user && !checking && !status && (
          <div style={{ ...card, textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: theme.grey550, marginBottom: 16 }}>{t('deleteAccount.statusFailed')}</p>
            <button onClick={loadStatus} style={{ background: 'none', border: `1px solid ${theme.sand}`, padding: '10px 24px', cursor: 'pointer', fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'inherit' }}>
              {t('deleteAccount.retry')}
            </button>
          </div>
        )}

        {user && !checking && status && status.can_delete && (
          <form onSubmit={submit} style={{ ...card, borderColor: theme.redBorder }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <ShieldCheck size={18} color={theme.redSoft} />
              <h2 style={{ fontFamily: theme.font.display, fontSize: 22, margin: 0 }}>
                {t('deleteAccount.confirmTitle')}
              </h2>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={label} htmlFor="gg-del-pw">{t('deleteAccount.passwordLabel')}</label>
              <input
                id="gg-del-pw"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                style={input}
              />
              <p style={{ fontSize: 12, color: theme.grey400, marginTop: 8 }}>{t('deleteAccount.passwordWhy')}</p>
            </div>

            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: theme.grey550, lineHeight: 1.6, marginBottom: 24, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span>{t('deleteAccount.acknowledge')}</span>
            </label>

            <button
              type="submit"
              disabled={!acknowledged || !password || working}
              style={{
                width: '100%', padding: '15px', border: 'none',
                background: (!acknowledged || !password || working) ? theme.redBorderRose : theme.redSoft,
                color: theme.white, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase',
                cursor: (!acknowledged || !password || working) ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {working ? t('deleteAccount.working') : t('deleteAccount.submit')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
